import { useEffect, useRef } from 'react'

interface SwipeGestureOptions {
  onSwipeLeft?: () => void
  onSwipeRight?: () => void
  threshold?: number // Minimum distance for swipe (default: 50px)
  restraint?: number // Maximum perpendicular distance (default: 100px)
  allowedTime?: number // Maximum time for swipe (default: 300ms)
}

/**
 * Hook to detect swipe gestures on touch devices
 * Supports left and right swipes with configurable thresholds
 */
export const useSwipeGesture = (
  elementRef: React.RefObject<HTMLElement>,
  options: SwipeGestureOptions
) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    threshold = 50,
    restraint = 100,
    allowedTime = 300
  } = options

  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null)

  useEffect(() => {
    const element = elementRef.current
    if (!element) return

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.changedTouches[0]
      touchStartRef.current = {
        x: touch.pageX,
        y: touch.pageY,
        time: Date.now()
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (!touchStartRef.current) return

      const touch = e.changedTouches[0]
      const distX = touch.pageX - touchStartRef.current.x
      const distY = touch.pageY - touchStartRef.current.y
      const elapsedTime = Date.now() - touchStartRef.current.time

      // Check if swipe meets criteria
      if (elapsedTime <= allowedTime) {
        // Check if horizontal distance is sufficient
        if (Math.abs(distX) >= threshold && Math.abs(distY) <= restraint) {
          if (distX < 0 && onSwipeLeft) {
            // Swipe left
            onSwipeLeft()
          } else if (distX > 0 && onSwipeRight) {
            // Swipe right
            onSwipeRight()
          }
        }
      }

      touchStartRef.current = null
    }

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [onSwipeLeft, onSwipeRight, threshold, restraint, allowedTime])
}
